import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return Date.now() >= decoded.exp * 1000;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const restoreSessionRef = useRef(false);

  const extractUser = (response) => {
    if (!response) return null;
    if (response.user) return response.user;
    if (response.data?.user) return response.data.user;
    if (response.data?.data) return response.data.data;
    if (response.email || response.id) return response;
    return null;
  };

  const restoreSession = useCallback(() => {
    if (restoreSessionRef.current) return;
    restoreSessionRef.current = true;

    const token = sessionStorage.getItem('token');
    const cachedUser = sessionStorage.getItem('user');

    if (!token) {
      setUser(null);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    if (isTokenExpired(token)) {
      sessionStorage.clear();
      setUser(null);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    if (cachedUser) {
      try {
        const userObj = JSON.parse(cachedUser);
        setUser(userObj);
      } catch {
        sessionStorage.removeItem('user');
      }
    }

    fetchUserFromServer();

    setLoading(false);
    setIsInitialized(true);
  }, []);

  const fetchUserFromServer = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = extractUser(response);
      if (userData && userData.email) {
        setUser(userData);
        sessionStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      console.error('Erreur /me:', err.message);
      const msg = err.message || '';
      const isAuthError =
        msg.includes('401') ||
        msg.includes('403') ||
        msg.includes('Unauthorized') ||
        msg.includes('Invalid token');
      if (isAuthError) {
        logout();
      }
    }
  };

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  const login = async (email, password) => {
    setError(null);
    try {
      const result = await authAPI.login({ email, password });

      if (result.requiresTwoFactor) {
        return result;
      }

      if (result.token) {
        sessionStorage.setItem('token', result.token);

        if (result.refreshToken) {
          sessionStorage.setItem('refreshToken', result.refreshToken);
        }

        const userData = extractUser(result);
        if (userData) {
          setUser(userData);
          sessionStorage.setItem('user', JSON.stringify(userData));
        }
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = useCallback(() => {
    sessionStorage.clear();
    setUser(null);
  }, []);

  const isAdmin = useCallback(() => {
    return user?.role === 'admin' || user?.role === 'Administrateur';
  }, [user]);

  const isModerator = useCallback(() => {
    return user?.role === 'admin' ||
      user?.role === 'Administrateur' ||
      user?.role === 'moderator' ||
      user?.role === 'Presentateur';
  }, [user]);

  const register = async (userData) => {
    setError(null);
    try {
      const result = await authAPI.register(userData);

      if (result.token) {
        sessionStorage.setItem('token', result.token);

        if (result.refreshToken) {
          sessionStorage.setItem('refreshToken', result.refreshToken);
        }

        const userObj = extractUser(result);
        if (userObj) {
          setUser(userObj);
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateProfile = async (updates) => {
    setError(null);
    try {
      const result = await authAPI.updateProfile(updates);
      if (result.user) {
        setUser(result.user);
        sessionStorage.setItem('user', JSON.stringify(result.user));
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setError(null);
    try {
      const result = await authAPI.changePassword(currentPassword, newPassword);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const setup2FA = async () => {
    setError(null);
    try {
      const result = await authAPI.sendOTP();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const enable2FA = async (token) => {
    setError(null);
    try {
      const result = await authAPI.enable2FA(token);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const disable2FA = async (password) => {
    setError(null);
    try {
      const result = await authAPI.disable2FA(password);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const verifyOTP = async (email, otp) => {
    setError(null);
    try {
      const result = await authAPI.verifyOTP(email, otp);

      if (result.token) {
        sessionStorage.setItem('token', result.token);

        if (result.refreshToken) {
          sessionStorage.setItem('refreshToken', result.refreshToken);
        }

        const userObj = extractUser(result);
        if (userObj) {
          setUser(userObj);
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resendOTP = async (email) => {
    setError(null);
    try {
      const result = await authAPI.resendOTP(email);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    isInitialized,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    setup2FA,
    enable2FA,
    disable2FA,
    verifyOTP,
    resendOTP,
    isAdmin,
    isModerator,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
